import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

export default function Services() {
  const services = [
    {
      icon: '💻',
      title: 'Web Development',
      description: 'Custom websites and web applications built with modern technologies and best practices.',
      features: [
        'Custom website design and development',
        'E-commerce platforms (Shopify, WooCommerce, custom)',
        'Progressive Web Apps (PWA)',
        'Content Management Systems (WordPress, custom CMS)',
        'API development and integration',
        'Responsive and mobile-first design'
      ],
      technologies: ['React', 'Node.js', 'Next.js', 'WordPress', 'PHP', 'Python'],
      useCases: ['Corporate Websites', 'E-commerce Stores', 'Web Applications', 'Landing Pages']
    },
    {
      icon: '📱',
      title: 'Mobile App Development',
      description: 'Native and cross-platform mobile applications for iOS and Android.',
      features: [
        'iOS app development (Swift, SwiftUI)',
        'Android app development (Kotlin, Java)',
        'Cross-platform apps (React Native, Flutter)',
        'App Store and Google Play deployment',
        'Push notifications and real-time features',
        'App maintenance and updates'
      ],
      technologies: ['React Native', 'Flutter', 'Swift', 'Kotlin', 'Firebase'],
      useCases: ['Business Apps', 'E-commerce Apps', 'Social Apps', 'Utility Apps']
    },
    {
      icon: '🎯',
      title: 'Digital Marketing',
      description: 'Comprehensive digital marketing strategies to grow your online presence.',
      features: [
        'Search Engine Optimization (SEO)',
        'Social media marketing and management',
        'Content marketing and strategy',
        'Pay-Per-Click (PPC) advertising',
        'Conversion rate optimization',
        'Analytics and performance tracking'
      ],
      technologies: ['Google Analytics', 'SEMrush', 'Hootsuite', 'Google Ads', 'Facebook Ads'],
      useCases: ['Brand Awareness', 'Lead Generation', 'Customer Acquisition', 'Engagement']
    },
    {
      icon: '📧',
      title: 'Email Marketing',
      description: 'Automated email campaigns that nurture leads and drive conversions.',
      features: [
        'Email campaign design and development',
        'Marketing automation workflows',
        'List segmentation and management',
        'A/B testing and optimization',
        'CAN-SPAM compliant campaigns',
        'Analytics and reporting'
      ],
      technologies: ['Mailchimp', 'SendGrid', 'HubSpot', 'ActiveCampaign'],
      useCases: ['Newsletter Campaigns', 'Drip Campaigns', 'Promotional Emails', 'Transactional Emails']
    },
    {
      icon: '☁️',
      title: 'Cloud Infrastructure',
      description: 'Scalable and secure cloud solutions for modern businesses.',
      features: [
        'Cloud architecture design',
        'AWS, Azure, and GCP deployment',
        'DevOps and CI/CD pipelines',
        'Server management and monitoring',
        'Security and compliance',
        'Backup and disaster recovery'
      ],
      technologies: ['AWS', 'Azure', 'GCP', 'Docker', 'Kubernetes', 'Terraform'],
      useCases: ['Application Hosting', 'Data Storage', 'Scalable Infrastructure', 'Microservices']
    },
    {
      icon: '💬',
      title: 'SMS Marketing',
      description: 'A2P 10DLC compliant SMS communication to engage with your customers.',
      features: [
        'SMS campaign strategy and execution',
        'A2P 10DLC registration assistance',
        'Automated messaging workflows',
        'Consent and opt-out management',
        'Real-time delivery tracking',
        'TCPA and CTIA compliance guidance'
      ],
      technologies: ['Twilio', 'Custom APIs', 'Automation Tools'],
      useCases: ['Customer Engagement', 'Appointment Reminders', 'Order Updates', 'Service Alerts']
    },
    {
      icon: '📊',
      title: 'Digital Campaigns',
      description: 'Multi-channel campaign management for maximum reach and impact.',
      features: [
        'Campaign strategy and planning',
        'Multi-channel execution (email, SMS, social)',
        'Audience targeting and segmentation',
        'Performance tracking and analytics',
        'A/B testing and optimization',
        'ROI measurement and reporting'
      ],
      technologies: ['HubSpot', 'Marketo', 'Google Analytics', 'Custom Dashboards'],
      useCases: ['Product Launches', 'Seasonal Promotions', 'Lead Generation', 'Re-engagement']
    },
    {
      icon: '📲',
      title: 'SMS Campaigns',
      description: 'TCPA compliant SMS messaging to communicate with your customers.',
      features: [
        'Campaign design and execution',
        'Consent management and documentation',
        'Message scheduling and automation',
        'Delivery reports and analytics',
        'Compliance guidance and support',
        'Carrier-approved messaging setup'
      ],
      technologies: ['Twilio', 'Automation Tools', 'Analytics Platforms'],
      useCases: ['Customer Communications', 'Service Notifications', 'Support Messages', 'Feedback Surveys']
    },
    {
      icon: '💰',
      title: 'Business Financing Referrals',
      description: 'Connect your business with financing options and funding opportunities.',
      features: [
        'Business loan referrals and connections',
        'Financing options from $50K to $5M+',
        'Working capital solutions',
        'Equipment financing referrals',
        'Line of credit connections',
        'Fast approval process coordination'
      ],
      technologies: ['Lending Network Partners', 'Financial Platforms', 'CRM Integration'],
      useCases: ['Business Expansion', 'Working Capital', 'Equipment Purchase', 'Cash Flow Management']
    }
  ];

  return (
    <>
      <Helmet>
        <title>Services - TEJ Solutions Hub | Web, Mobile, Marketing, Cloud & Financing</title>
        <meta name="description" content="Comprehensive software development, digital marketing, and business financing services. Web development, mobile apps, digital marketing, cloud infrastructure, SMS campaigns, and business financing referrals." />
      </Helmet>

      <div style={{ paddingTop: '100px', background: '#f9fafb', minHeight: '100vh' }}>
        <div style={{ background: 'linear-gradient(135deg, #7c3aed 0%, #a78bfa 100%)', padding: '4rem 2rem', color: 'white', textAlign: 'center' }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <div style={{ display: 'inline-block', background: 'rgba(255,255,255,0.2)', padding: '0.5rem 1.5rem', borderRadius: '50px', marginBottom: '1.5rem', border: '1px solid rgba(255,255,255,0.3)' }}>
              <span style={{ fontSize: '0.9rem', fontWeight: '600' }}>✓ Full-Service Technology Solutions</span>
            </div>
            <h1 style={{ fontSize: '3rem', fontWeight: '800', marginBottom: '1rem' }}>Our Services</h1>
            <p style={{ fontSize: '1.25rem', opacity: '0.95', maxWidth: '700px', margin: '0 auto' }}>
              Comprehensive solutions to transform your business through technology
            </p>
          </div>
        </div>

        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '4rem 2rem' }}>
          <div style={{ display: 'grid', gap: '3rem' }}>
            {services.map((service, idx) => (
              <div key={idx} style={{ background: 'white', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', border: '2px solid #e5e7eb' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '0' }}>
                  <div style={{ padding: '3rem', background: 'white' }}>
                    <div style={{ fontSize: '4rem', marginBottom: '1.5rem' }}>{service.icon}</div>
                    <h2 style={{ fontSize: '2rem', fontWeight: '700', color: '#7c3aed', marginBottom: '1rem' }}>
                      {service.title}
                    </h2>
                    <p style={{ fontSize: '1.1rem', color: '#6b7280', lineHeight: '1.8', marginBottom: '2rem' }}>
                      {service.description}
                    </p>
                    
                    <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#1f2937', marginBottom: '1rem' }}>
                      What We Offer:
                    </h3>
                    <ul style={{ fontSize: '1rem', color: '#4b5563', lineHeight: '2', paddingLeft: '1.5rem', marginBottom: '2rem' }}>
                      {service.features.map((feature, i) => (
                        <li key={i}>{feature}</li>
                      ))}
                    </ul>

                    <div style={{ marginBottom: '2rem' }}>
                      <h4 style={{ fontSize: '1rem', fontWeight: '600', color: '#1f2937', marginBottom: '0.75rem' }}>
                        Technologies:
                      </h4>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                        {service.technologies.map((tech, i) => (
                          <span key={i} style={{ background: '#f3e8ff', color: '#7c3aed', padding: '0.25rem 0.75rem', borderRadius: '20px', fontSize: '0.85rem', fontWeight: '500' }}>
                            {tech}
                          </span>
                        ))}
                      </div>
                    </div>

                    <Link to="/contact" style={{
                      display: 'inline-block',
                      background: 'linear-gradient(135deg, #7c3aed 0%, #a78bfa 100%)',
                      color: 'white',
                      padding: '1rem 2rem',
                      borderRadius: '8px',
                      textDecoration: 'none',
                      fontWeight: '600',
                      transition: 'all 0.3s ease'
                    }}>
                      Get Started →
                    </Link>
                  </div>

                  <div style={{ background: 'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)', padding: '3rem', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#1f2937', marginBottom: '1.5rem' }}>
                      Perfect For:
                    </h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                      {service.useCases.map((useCase, i) => (
                        <div key={i} style={{ background: 'white', padding: '1rem 1.5rem', borderRadius: '8px', border: '2px solid #7c3aed', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                          <span style={{ fontSize: '1.5rem' }}>✓</span>
                          <span style={{ fontSize: '1rem', fontWeight: '500', color: '#1f2937' }}>{useCase}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div style={{ background: 'white', padding: '4rem 2rem', borderTop: '1px solid #e5e7eb' }}>
          <div style={{ maxWidth: '900px', margin: '0 auto', textAlign: 'center' }}>
            <h2 style={{ fontSize: '2.5rem', fontWeight: '800', color: '#1f2937', marginBottom: '1rem' }}>
              Ready to Get Started?
            </h2>
            <p style={{ fontSize: '1.1rem', color: '#6b7280', marginBottom: '2rem' }}>
              Contact us today for a free consultation and project quote
            </p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link to="/contact" style={{
                display: 'inline-block',
                background: 'linear-gradient(135deg, #7c3aed 0%, #a78bfa 100%)',
                color: 'white',
                padding: '1.25rem 3rem',
                borderRadius: '12px',
                textDecoration: 'none',
                fontWeight: '700',
                fontSize: '1.1rem',
                boxShadow: '0 8px 24px rgba(124, 58, 237, 0.3)'
              }}>
                Contact Us
              </Link>
              <Link to="/pricing" style={{
                display: 'inline-block',
                background: 'white',
                color: '#7c3aed',
                padding: '1.25rem 3rem',
                borderRadius: '12px',
                textDecoration: 'none',
                fontWeight: '700',
                fontSize: '1.1rem',
                border: '2px solid #7c3aed'
              }}>
                View Pricing
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
